/**
 * Which camera streams the SFU should keep sending — the lastN decision.
 *
 * Lifted out of the effect in `useConsumers` unchanged. The decision is worth
 * having on its own because it is the one place that answers "why did that tile
 * freeze", and inside a hook effect it could only be checked by reading it: a
 * fifty-person room is not something a test can convene, and it is exactly the
 * size where the answer stops being obvious.
 *
 * The caller still owns the side effects — issuing pause/resume, requesting the
 * keyframes a resumed stream needs, logging. This only decides.
 */

import { statsKey } from './rtp-stats'

/**
 * The slice of a consumer entry this needs, declared structurally so a test can
 * hand it plain objects rather than mediasoup Consumers.
 */
export type LastNEntry = {
  peerId: string
  kind: 'audio' | 'video'
  sourceType: string
  /**
   * What we last told the SFU about this consumer, or undefined if we have
   * never spoken about it. Carrying the answer forward is what keeps this to
   * transitions: without it, every recompute of the visible set would re-pause
   * everything already paused and re-request keyframes for everything already
   * flowing.
   */
  lastNVisible?: boolean
  /**
   * The same idea for the audio cap, kept separate because the two caps answer
   * different questions and a consumer is only ever subject to one of them.
   */
  audioAudible?: boolean
}

export type LastNDecision = {
  /** Consumer ids to resume, and to request keyframes for. */
  resume: string[]
  /** Consumer ids to pause at the SFU. */
  pause: string[]
}

/**
 * Can this consumer be capped by lastN?
 *
 * Cameras and extra camera inputs: yes. Screen share: never — a share is
 * usually the thing everyone is looking at.
 *
 * Audio: never by THIS cap, which is keyed on what is visible, and someone
 * off-screen still has to be audible. That is not the same as audio being
 * uncapped: it has its own cap, `decideAudioCap` below, ranked by who is
 * speaking rather than by who is on screen. The old note here read "audio is a
 * fraction of the bandwidth", which was the whole justification and stopped
 * being true somewhere between a normal meeting and 150 people.
 *
 * Kept in step with `isPausableVideoSource` in useConsumers, which answers the
 * same question for the "pause all incoming videos" control.
 */
function cappable(kind: string, sourceType: string): boolean {
  if (kind !== 'video') return false
  return sourceType !== 'screen' && sourceType !== 'screenshare'
}

/**
 * Decide what to pause and what to resume, and record the new answer on each
 * entry.
 *
 * `visible` is keyed by SOURCE, not by peer: someone's camera and their
 * document camera are separate tiles that can be on screen independently, so
 * pausing both because "the peer is visible" would be wrong in both directions.
 *
 * Mutates `lastNVisible` on the entries it decides about — deliberately, since
 * that flag is the memory that makes this a transition rather than a re-assert,
 * and the caller would otherwise have to duplicate the bookkeeping.
 */
export function decideLastN(
  entries: Iterable<readonly [string, LastNEntry]>,
  visible: ReadonlySet<string>,
): LastNDecision {
  const decision: LastNDecision = { resume: [], pause: [] }

  for (const [consumerId, entry] of entries) {
    if (!cappable(entry.kind, entry.sourceType)) continue

    const wanted = visible.has(statsKey(entry.peerId, entry.sourceType, 'video'))
    // Already in the state we want it in. Saying so again costs a round trip
    // and, on the resume side, a keyframe.
    if (wanted === entry.lastNVisible) continue

    entry.lastNVisible = wanted
    if (wanted) decision.resume.push(consumerId)
    else decision.pause.push(consumerId)
  }

  return decision
}

/**
 * Undo every pause this cap has issued.
 *
 * Used when the cap disengages — nothing painted yet, or rendered-tiles found
 * key drift and fell open. Disengaging has to UNDO the pauses, not merely stop
 * issuing new ones: a consumer paused a moment before drift was noticed would
 * otherwise stay paused for the rest of the call, which is how "pausing is
 * disabled rather than freezing that tile" still froze the tile.
 */
export function releaseLastN(
  entries: Iterable<readonly [string, LastNEntry]>,
): string[] {
  const resume: string[] = []
  for (const [consumerId, entry] of entries) {
    if (entry.lastNVisible === false) {
      entry.lastNVisible = undefined
      resume.push(consumerId)
    }
  }
  return resume
}

/**
 * "Pause all incoming videos" toggled: which camera consumers to pause or
 * resume.
 *
 * Unpausing resumes only what lastN has not itself paused. It used to resume
 * every camera, including the ones lastN was holding off-screen — and since
 * their latch still read `false`, `decideLastN` saw nothing to change and never
 * paused them again, so they streamed to a tile nobody could see until the grid
 * next moved. A consumer that arrived during the pause has no latch at all and
 * is resumed here; the lastN pass that follows pauses it if it is off-screen.
 *
 * Latches are left alone in both directions: lastN stands down while the pause
 * is on, and picks up from its own record when it lifts.
 */
export function decideIncomingVideoPause(
  entries: Iterable<readonly [string, LastNEntry]>,
  paused: boolean,
): LastNDecision {
  const decision: LastNDecision = { resume: [], pause: [] }
  for (const [consumerId, entry] of entries) {
    if (!cappable(entry.kind, entry.sourceType)) continue
    if (paused) decision.pause.push(consumerId)
    else if (entry.lastNVisible !== false) decision.resume.push(consumerId)
  }
  return decision
}

/**
 * A remote camera came back on: may we resume our consumer of it?
 *
 * Not if something on this side is deliberately holding it — "pause all
 * incoming videos", or lastN keeping the tile off-screen. The producer coming
 * back is the publisher's decision; whether THIS viewer receives it is ours,
 * and resuming here was how a camera turning on quietly undid the user's pause:
 * the tile stayed masked while the SFU started forwarding to it again.
 */
export function shouldResumeOnProducerResume(
  entry: LastNEntry,
  incomingVideoPaused: boolean,
): boolean {
  if (incomingVideoPaused && cappable(entry.kind, entry.sourceType)) return false
  return entry.lastNVisible !== false
}

/**
 * How many audio streams stay flowing once the cap engages.
 *
 * Audio was deliberately exempt from lastN, on the reasoning stated above it:
 * "someone off-screen still has to be audible, and audio is a fraction of the
 * bandwidth". The first half is still true and this preserves it. The second
 * half stops being true with enough people in the room, and nothing noticed
 * when it did.
 *
 * Measured in a ~150-peer room: 144 of 144 audio consumers resumed and
 * receiving, 89.2 MB cumulative, against 134 video consumers of which lastN was
 * holding all but ~16 paused. Audio was the larger stream count AND the
 * uncapped one, and every one of those consumers is packet forwarding on the
 * single mediasoup worker that the room's router is pinned to.
 *
 * 20 is chosen to be far above any real conversation — a meeting with more than
 * twenty people genuinely talking at once has a different problem — so that in
 * practice nobody is ever cut off mid-sentence.
 */
export const AUDIO_CAP = 20

/**
 * Below this many audio consumers the cap does not engage at all.
 *
 * INFINITY — the cap OFF, every voice flowing — again since 2026-10-05.
 *
 * What changed underneath it: the microphone is now published on the first
 * unmute, not at join (useLocalMedia.ts `startAudioProducer`). The ~290 audio
 * receivers below were one per PARTICIPANT, almost all of them for people who
 * joined muted and never spoke. Now there is one per participant who has
 * unmuted at least once, and a muted one sends no RTP at all (`zeroRtpOnPause`,
 * plus the server-side producer pause), so the live forwarding the cap was
 * saving is bounded by how many people are unmuted at once — a handful in a
 * real meeting. Against that, the cap's cost is a real voice going silent
 * because twenty others spoke more recently.
 *
 * The one case it still covered: an unmuted microphone left open sends at the
 * full Opus rate even in silence, because `opusDtx` is off by design. A room
 * with more than ~25 open microphones pays for that now.
 *
 * Kept as a threshold rather than deleted, because the effect that applies it
 * is also what resumes audio when "pause all incoming audio" is lifted.
 *
 * History. Off (Infinity) on 2026-09-17, on the grounds that a voice cut
 * because twenty other people spoke more recently costs more than the
 * forwarding it saves. Re-engaged at 25 on 2026-09-22, when a 300-person room
 * — with every participant still publishing a mic at join — measured every
 * participant holding ~290 live Opus receivers and ~290 hidden <audio>
 * elements: roughly 14,500 RTP packets/s to depacketize and decode and 8-12
 * Mbps inbound, MORE than all the video combined, and ~98,000 consumers on the
 * SFU, nearly all of them audio. 25 sat above AUDIO_CAP (20) deliberately, so
 * there was a band where nothing happened rather than a cliff at the cap. If
 * it is ever re-engaged, that is the number to start from.
 *
 * The property that makes this safe is in the doc block below: pausing OUR
 * consumer does not hide a speaker from the SFU's detector, which watches
 * PRODUCERS. Someone we have stopped listening to who starts talking still
 * appears in the next active-speaker push, climbs the ranking, and is resumed.
 */
export const AUDIO_CAP_ENGAGE_ABOVE = Infinity

/**
 * Which audio consumers stay resumed, ranked by who is actually speaking.
 *
 * `ranked` is the active-speaker list, most recently dominant first — the same
 * list `useActiveSpeakers` already computes and, until now, only used to pick a
 * tile to highlight.
 *
 * The property that makes this safe is easy to miss: pausing OUR consumer does
 * not hide a speaker from the server's detector. The SFU's AudioLevelObserver
 * watches PRODUCERS (`room.js:454-456` adds each audio producer to it), and a
 * producer is unaffected by what any individual receiver has paused. So a peer
 * we have paused who then starts talking still shows up in the next
 * active-speaker push, still climbs the ranking, and is resumed. If the
 * detector had observed consumers instead, this would be a trap: the people we
 * stopped listening to could never be discovered again.
 *
 * Mutates `audioAudible`, for the same reason `decideLastN` mutates
 * `lastNVisible` — the flag is the memory that makes this a transition.
 */
export function decideAudioCap(
  entries: Iterable<readonly [string, LastNEntry]>,
  ranked: readonly string[] = [],
  opts: { cap?: number; engageAbove?: number } = {},
): LastNDecision {
  const cap = opts.cap ?? AUDIO_CAP
  const engageAbove = opts.engageAbove ?? AUDIO_CAP_ENGAGE_ABOVE
  const decision: LastNDecision = { resume: [], pause: [] }

  const audio = [...entries].filter(([, e]) => e.kind === 'audio')
  // Small room: everything audible, exactly as before this existed.
  const wantAll = audio.length <= engageAbove

  // Nothing below depends on the ORDER when every entry is wanted, and the sort
  // is the expensive half: at ~290 entries it is ~2,370 comparisons, each doing
  // two Map lookups, run on every active-speaker push — to compute an answer
  // that is "resume everything" whatever the ranking says.
  if (wantAll) {
    for (const [consumerId, entry] of audio) {
      if (entry.audioAudible === true) continue
      entry.audioAudible = true
      decision.resume.push(consumerId)
    }
    return decision
  }

  const rank = new Map<string, number>()
  ranked.forEach((peerId, i) => { if (!rank.has(peerId)) rank.set(peerId, i) })

  const ordered = audio.slice().sort((a, b) => {
    const ra = rank.get(a[1].peerId) ?? Number.MAX_SAFE_INTEGER
    const rb = rank.get(b[1].peerId) ?? Number.MAX_SAFE_INTEGER
    if (ra !== rb) return ra - rb
    // Stable beyond the ranked set, so an unranked majority does not reshuffle
    // on every recompute and thrash pause/resume against the control plane.
    return a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0
  })

  ordered.forEach(([consumerId, entry], index) => {
    const wanted = wantAll || index < cap
    if (wanted === entry.audioAudible) return
    entry.audioAudible = wanted
    if (wanted) decision.resume.push(consumerId)
    else decision.pause.push(consumerId)
  })

  return decision
}

/**
 * Can "pause all incoming audio" pause this consumer?
 *
 * Voices, whichever way they arrived — a browser mic (`camera`), an H.323 or
 * SIP endpoint. Not a share's audio (`screen` / `screenshare`): the video twin
 * exempts shares for the same reason, the presentation is why you are there.
 * Not `composited`, which the client never consumes.
 *
 * Kept in step with `isPausableAudioSource` in useConsumers, which answers the
 * same question for new arrivals and the reconnect re-assert.
 */
function pausableAudio(kind: string, sourceType: string): boolean {
  if (kind !== 'audio') return false
  return sourceType !== 'screen' && sourceType !== 'screenshare' && sourceType !== 'composited'
}

/**
 * "Pause all incoming audio" — which consumers to pause, and the latch it has
 * to clear either way.
 *
 * Returns the ids to pause at the SFU when `paused`, and nothing when not:
 * unpausing deliberately resumes NOTHING here. `decideAudioCap` does it on its
 * next pass, which is what keeps a 150-peer room at AUDIO_CAP streams instead
 * of resuming all of them, and what picks up consumers that arrived — and were
 * held paused — while the toggle was on.
 *
 * That hand-off only works because `audioAudible` is cleared to `undefined`
 * here. A latch left at `true` is the trap: `decideAudioCap` skips anything
 * already in the state it wants, so it would never re-issue the resume and
 * that person would stay silent for the rest of the call. `undefined`, not
 * `false`, for the reason `resumeAndUnlatch` gives — false would claim the cap
 * decided to pause it.
 *
 * Share audio keeps its latch: the pause never touched it.
 */
export function decideIncomingAudioPause(
  entries: Iterable<readonly [string, LastNEntry]>,
  paused: boolean,
): string[] {
  const pause: string[] = []
  for (const [consumerId, entry] of entries) {
    if (!pausableAudio(entry.kind, entry.sourceType)) continue
    entry.audioAudible = undefined
    if (paused) pause.push(consumerId)
  }
  return pause
}

/**
 * Undo every pause the audio cap issued — for when a room shrinks back below
 * the engage threshold, or the cap is turned off.
 *
 * Same shape, and the same reasoning, as `releaseLastN`: disengaging has to UNDO,
 * not merely stop issuing. A cap that stops deciding while its pauses stand is
 * how someone stays inaudible for the rest of a call.
 */
export function releaseAudioCap(
  entries: Iterable<readonly [string, LastNEntry]>,
): string[] {
  const resume: string[] = []
  for (const [consumerId, entry] of entries) {
    if (entry.kind !== 'audio') continue
    if (entry.audioAudible === false) {
      entry.audioAudible = undefined
      resume.push(consumerId)
    }
  }
  return resume
}
